import React from 'react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import AdminList, {
    NameCellRenderer,
    sortColByStringProp,
} from '../../ui/admin/AdminList';
import { useQuery } from '@apollo/react-hooks';
import { Template } from '../../../shared/types';
import { formatDisplayDate } from '../../../utils/date.utils';
import gql from 'graphql-tag';
import { sortArray } from '../../../utils/sorting.utils';
import {
    premiumChannelsEnabled,
    topicsOrCategories,
} from '../../../utils/utils';
import { TextOverlapping } from '../../ui/TextOverlapping';
import { Box } from '@mui/material';

const name = 'Templates';
const apiName = 'templates';

const TEMPLATES = gql`
    query getTemplates {
        templates {
            _id
            title
            categories {
                _id
                name
            }
            tags {
                _id
                name
            }
            publishedBy {
                _id
                name
                type
            }
            created
            lastEdited
            usedArticlesNumber
            channel {
                _id
                name
            }
        }
    }
`;

const TemplateList = () => {
    const { loading, data } = useQuery(TEMPLATES, {
        fetchPolicy: 'network-only',
    });
    const templates = (data?.templates as Template[]) || [];
    const sortedTemplates = sortArray(templates, 'title', true);

    const columnDefs: ColDef[] = [
        {
            headerName: 'Template name',
            field: 'title',
            width: 384,
            cellStyle: { overflow: 'hidden' },
            tooltipField: 'title',
            cellRenderer: (props: any) => (
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                        '& a, & strong': {
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        },
                    }}
                >
                    <NameCellRenderer
                        wrap={false}
                        {...props}
                        apiName={apiName}
                    />
                </Box>
            ),
            menuTabs: [],
        },
        {
            headerName: 'Publisher',
            field: 'publishedBy',
            width: 200,
            comparator: sortColByStringProp('name'),
            valueFormatter: (props: any) => props.data?.publishedBy?.name,
            menuTabs: [],
        },
        {
            headerName: topicsOrCategories(false, true),
            field: 'categories',
            autoHeight: true,
            cellRenderer: (props: ICellRendererParams) => {
                const categories = props.value
                    .map((category: any) => category.name)
                    .join(', ');

                return (
                    <TextOverlapping
                        value={categories}
                        maxLength={20}
                        maxWidth={150}
                    />
                );
            },
            menuTabs: [],
        },
        ...(premiumChannelsEnabled(false)
            ? [
                  {
                      headerName: 'Channel',
                      field: 'channel',
                      width: 200,
                      valueFormatter: (props: any) => props.data?.channel?.name,
                      menuTabs: [],
                  },
              ]
            : []),
        {
            headerName: '# used',
            field: 'used',
            width: 96,
            valueFormatter: (props: any) => {
                const numArticles = props.data?.usedArticlesNumber ?? 0;
                return numArticles === 0
                    ? 'no articles'
                    : numArticles === 1
                      ? '1 article'
                      : `${numArticles} articles`;
            },
            menuTabs: [],
        },
        {
            headerName: 'Created',
            field: 'created',
            width: 140,
            valueFormatter: ({ data }) => formatDisplayDate(data.created) ?? '',
            menuTabs: [],
        },
        {
            headerName: 'Last Edit',
            field: 'lastEdited',
            width: 140,
            valueFormatter: ({ data }) => getEditDate(data),
            menuTabs: [],
        },
    ];

    return (
        <AdminList
            title={`Article ${name.toLocaleLowerCase()}`}
            name={name}
            entityForCaptionText={name}
            items={sortedTemplates}
            loading={loading}
            columns={columnDefs}
            showAdd={true}
            addButtonLabel="New template"
        />
    );
};

function getEditDate(template: Template): string {
    if (!template || !template.lastEdited) {
        return '';
    }
    return (
        (template.lastEdited && formatDisplayDate(template.lastEdited)) ?? ''
    );
}

export default TemplateList;
